import React, { useRef, useState } from "react";
import { Upload, Trash2, ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";

const MAX_WIDTH = 1200;
const MAX_HEIGHT = 900;
const MAX_BASE64_SIZE = 500 * 1024; // ~500 KB target for base64 string

/**
 * Compress an image file to a JPEG data URL under MAX_BASE64_SIZE.
 * Returns { dataUrl, sizeKB, width, height }.
 */
async function compressImage(file) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const reader = new FileReader();
        reader.onload = (e) => {
            img.onload = () => {
                let { width, height } = img;
                const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height, 1);
                width = Math.round(width * ratio);
                height = Math.round(height * ratio);
                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, width, height);
                // Iteratively lower quality until under threshold
                let quality = 0.85;
                let dataUrl = canvas.toDataURL("image/jpeg", quality);
                while (dataUrl.length > MAX_BASE64_SIZE && quality > 0.4) {
                    quality -= 0.1;
                    dataUrl = canvas.toDataURL("image/jpeg", quality);
                }
                resolve({ dataUrl, sizeKB: Math.round(dataUrl.length / 1024), width, height });
            };
            img.onerror = () => reject(new Error("Impossible de lire l'image."));
            img.src = e.target.result;
        };
        reader.onerror = () => reject(new Error("Lecture du fichier impossible."));
        reader.readAsDataURL(file);
    });
}

/**
 * Reusable image picker with client-side compression.
 * Props:
 *   value: string | null (data URL or http URL)
 *   onChange: (nextValue: string | null) => void
 *   testId?: string prefix (default "image-upload")
 */
export default function ImageUpload({ value, onChange, testId = "image-upload" }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);

    const handleFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Veuillez sélectionner un fichier image.");
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            toast.error("L'image dépasse 8 Mo. Choisissez une image plus légère.");
            return;
        }
        setUploading(true);
        try {
            const { dataUrl, sizeKB } = await compressImage(file);
            onChange(dataUrl);
            toast.success(`Image ajoutée (${sizeKB} Ko après compression)`);
        } catch (e) {
            toast.error(e.message || "Impossible de traiter l'image.");
        } finally {
            setUploading(false);
        }
    };

    const onFileInput = (e) => {
        const f = e.target.files?.[0];
        handleFile(f);
        e.target.value = ""; // allow re-select same file
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        handleFile(f);
    };

    const hasImage = !!(value && value.trim());

    return (
        <div data-testid={testId}>
            {hasImage ? (
                <div
                    className="relative border p-2"
                    style={{ borderColor: "var(--bc-border)", background: "#0a0a0a" }}
                >
                    <img
                        src={value}
                        alt="Aperçu"
                        className="w-full max-h-64 object-cover"
                        data-testid={`${testId}-preview`}
                    />
                    <div className="mt-2 flex gap-2 flex-wrap">
                        <button
                            type="button"
                            className="bc-btn bc-btn--sm bc-btn--ghost"
                            onClick={() => inputRef.current?.click()}
                            disabled={uploading}
                            data-testid={`${testId}-replace`}
                        >
                            <Upload size={12} /> Remplacer
                        </button>
                        <button
                            type="button"
                            className="bc-btn bc-btn--sm bc-btn--danger"
                            onClick={() => onChange(null)}
                            data-testid={`${testId}-remove`}
                        >
                            <Trash2 size={12} /> Retirer
                        </button>
                    </div>
                </div>
            ) : (
                <div
                    className="border-2 border-dashed p-6 text-center cursor-pointer transition-colors"
                    style={{
                        borderColor: dragOver ? "var(--bc-gold)" : "var(--bc-border)",
                        background: dragOver ? "rgba(212,188,124,0.08)" : "#0a0a0a",
                    }}
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    data-testid={`${testId}-dropzone`}
                >
                    {uploading ? (
                        <div className="flex flex-col items-center gap-2 text-[#D4BC7C]">
                            <Loader2 size={22} className="animate-spin" />
                            <p className="text-xs">Compression en cours…</p>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center gap-2">
                            <ImageIcon size={22} className="text-[#D4BC7C]" />
                            <p className="text-sm text-[#F2ECE0]">
                                Cliquez ou glissez une image ici
                            </p>
                            <p className="text-[10px] text-[#A0A0A0] uppercase tracking-widest">
                                JPG · PNG · WEBP · 8 Mo max — auto-compressée
                            </p>
                        </div>
                    )}
                </div>
            )}

            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onFileInput}
                data-testid={`${testId}-input`}
            />
        </div>
    );
}
